import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import { qk } from '../keys';
import type { DocumentStatus, DocumentSummary, Paginated } from '../types';

export function useDocumentQueue(status: DocumentStatus = 'pending_review') {
  return useQuery({
    queryKey: qk.documents.queue(status),
    queryFn: () => api.get<Paginated<DocumentSummary>>('/documents', { status, perPage: 100 }),
    staleTime: 15_000,
  });
}

export function useReviewDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; accept: boolean; reason?: string; reshoot?: boolean }) =>
      api.post<DocumentSummary>(`/documents/${input.id}/review`, {
        decision: input.accept ? 'accept' : 'reject',
        ...(input.accept ? {} : { reason: input.reason, reshoot: input.reshoot ?? true }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.documents.all });
      // Приетият документ може да отключи „готова за фактуриране".
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      // Отметките в курсовия лист на буса и сигналите за документи.
      void queryClient.invalidateQueries({ queryKey: qk.trips.all });
      void queryClient.invalidateQueries({ queryKey: qk.anomalies.all });
    },
  });
}

/** Всички файлове на заявка, курс, бус или шофьор — с линк за преглед. */
export function useDocumentsOf(filter: {
  orderId?: string;
  tripId?: string;
  vehicleId?: string;
  driverId?: string;
  /** Няколко вида, със запетая: `id_card,driving_licence`. */
  types?: string;
}) {
  const clean = Object.fromEntries(Object.entries(filter).filter(([, v]) => Boolean(v))) as Record<string, string>;
  return useQuery({
    queryKey: qk.documents.of(clean),
    queryFn: () => api.get<Paginated<DocumentSummary>>('/documents', { ...clean, perPage: 100 }),
    enabled: Object.keys(clean).length > 0,
  });
}

/**
 * Фактура от системата: чернова от доставената заявка → издаване → PDF.
 * Сървърът спира, ако заявката още не е готова (липсва ЧМР или разписка) —
 * съобщението му е на български и стига до диспечера както е.
 */
export function useIssueInvoice(orderId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const draft = await api.post<{ id: string; number: string | null }>('/invoices', { orderIds: [orderId] });
      const issued = await api.post<{ id: string; number: string | null }>(`/invoices/${draft.id}/issue`, {});
      await api.post(`/invoices/${draft.id}/pdf`, {});
      return issued;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.orders.all });
      void queryClient.invalidateQueries({ queryKey: qk.documents.all });
      void queryClient.invalidateQueries({ queryKey: qk.trips.all });
    },
  });
}

/** До кога важи документът и бележка — за вече качен файл. */
export function useDocumentMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, validUntil, notes }: { id: string; validUntil?: string | null; notes?: string | null }) =>
      api.post<DocumentSummary>(`/documents/${id}/meta`, {
        ...(validUntil !== undefined ? { validUntil } : {}),
        ...(notes !== undefined ? { notes } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.documents.all });
      void queryClient.invalidateQueries({ queryKey: qk.drivers.all });
      void queryClient.invalidateQueries({ queryKey: ['dossier'] });
      void queryClient.invalidateQueries({ queryKey: ['anomalies'] });
    },
  });
}
